import {useCallback, useEffect, useRef, useState} from 'react';
import {useAppSelector} from '@/hooks';
import type {ModalStorage} from '@Components/modal-container/modal-container-reducer';
import type {CoverPhotoPickerLoading, MediaTypes} from './event-wizard-image-video.types';
import {LEGACY_DIALOG_OPEN_BODY_CLASS, OPEN_MODAL_IDS_SEPARATOR} from './event-wizard-image-video.types';

/**
 * Which cover source is opening, from the click until its picker is on screen. Pickers open three ways: a React modal
 * (Uploads, My designs), a legacy dialog that marks the body (the AI designs gallery), or the embedded editor, which
 * reports itself through stopLoading. Any of those, or a dialog closing, ends the loading.
 */
export function useCoverPhotoPickerLoading(): CoverPhotoPickerLoading {
  const [loadingMediaType, setLoadingMediaType] = useState<MediaTypes | null>(null);
  const openModalIds = useAppSelector(({modals}) => {
    return getOpenModalIds(modals.modalsHashmap);
  });
  const openModalIdsWhenLoadingStarted = useRef('');
  const hadLegacyDialogOpenWhenLoadingStarted = useRef(false);

  const stopLoading = useCallback((): void => {
    setLoadingMediaType(null);
    window.removeEventListener(window.PMW.getEventDialogCloseEvent(), stopLoading);
  }, []);

  const startLoading = (mediaType: MediaTypes): void => {
    openModalIdsWhenLoadingStarted.current = openModalIds;
    hadLegacyDialogOpenWhenLoadingStarted.current = isLegacyDialogOpen();
    setLoadingMediaType(mediaType);
    window.addEventListener(window.PMW.getEventDialogCloseEvent(), stopLoading);
  };

  useEffect(() => {
    if (loadingMediaType !== null && hasModalOpenedSince(openModalIdsWhenLoadingStarted.current, openModalIds)) {
      stopLoading();
    }
  }, [loadingMediaType, openModalIds, stopLoading]);

  useEffect(() => {
    if (loadingMediaType === null || hadLegacyDialogOpenWhenLoadingStarted.current) {
      return undefined;
    }
    // A fast dialog can open before this effect runs, so check once before waiting for the change.
    if (isLegacyDialogOpen()) {
      stopLoading();
      return undefined;
    }
    const bodyClassObserver = new MutationObserver((): void => {
      if (isLegacyDialogOpen()) {
        stopLoading();
      }
    });
    bodyClassObserver.observe(document.body, {attributes: true, attributeFilter: ['class']});
    return (): void => {
      bodyClassObserver.disconnect();
    };
  }, [loadingMediaType, stopLoading]);

  useEffect(() => {
    return (): void => {
      window.removeEventListener(window.PMW.getEventDialogCloseEvent(), stopLoading);
    };
  }, [stopLoading]);

  return {loadingMediaType, startLoading, stopLoading};
}

/** A primitive key, so the selector does not re-render the caller when the open set has not changed. */
function getOpenModalIds(modalsHashmap: Record<string, ModalStorage>): string {
  return Object.entries(modalsHashmap)
    .filter(([, modal]) => {
      return modal.isOpen;
    })
    .map(([modalId]) => {
      return modalId;
    })
    .sort()
    .join(OPEN_MODAL_IDS_SEPARATOR);
}

/** True when a modal is open now that was not open before; one closing (a phone's menu sheet) does not count. */
function hasModalOpenedSince(openModalIdsBefore: string, openModalIdsNow: string): boolean {
  const modalIdsBefore = openModalIdsBefore.split(OPEN_MODAL_IDS_SEPARATOR);
  return openModalIdsNow.split(OPEN_MODAL_IDS_SEPARATOR).some((modalId) => {
    return modalId !== '' && !modalIdsBefore.includes(modalId);
  });
}

function isLegacyDialogOpen(): boolean {
  return document.body.classList.contains(LEGACY_DIALOG_OPEN_BODY_CLASS);
}
